// Admin Panel - Task Details Page with Real-time Updates
// File: admin-panel/src/pages/TaskDetails.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase'
import {
  ArrowLeftIcon,
  TruckIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  DocumentTextIcon,
  CameraIcon,
  CurrencyRupeeIcon,
  ExclamationTriangleIcon,
  MapIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const StatusBadge = ({ status }) => {
  const colors = {
    unassigned: 'bg-gray-100 text-gray-700 border-gray-300',
    pending: 'bg-yellow-50 text-yellow-700 border-yellow-300',
    accepted: 'bg-blue-50 text-blue-700 border-blue-300',
    picked_up: 'bg-purple-50 text-purple-700 border-purple-300',
    in_transit: 'bg-indigo-50 text-indigo-700 border-indigo-300',
    delivered: 'bg-green-100 text-green-700 border-green-300',
    failed: 'bg-red-100 text-red-700 border-red-300',
  };
  
  return (
    <span className={`px-4 py-2 rounded-xl text-sm font-bold border ${colors[status] || colors.pending}`}>
      {status?.replace('_', ' ').toUpperCase()}
    </span>
  );
};

const PaymentBadge = ({ mode, collected }) => {
  const colors = {
    cash: 'bg-yellow-50 text-yellow-700',
    online: 'bg-blue-50 text-blue-700',
    prepaid: 'bg-green-100 text-green-700',
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className={`px-3 py-1 rounded-lg text-sm font-medium ${colors[mode] || 'bg-gray-100 text-gray-600'}`}>
        {mode?.toUpperCase()}
      </span>
      {collected && (
        <span className="px-3 py-1 rounded-lg text-sm font-medium bg-green-100 text-green-700">
          ✓ COLLECTED
        </span>
      )}
    </div>
  );
};

const PhotoGallery = ({ title, photos, icon: Icon }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  if (!photos || photos.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <Icon className="w-5 h-5 text-gray-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        </div>
        <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <p className="text-gray-500">No photos uploaded</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-600">
          {photos.length} photo{photos.length > 1 ? 's' : ''}
        </span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {photos.map((photo, index) => (
          <div
            key={index}
            className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden cursor-pointer group shadow-sm"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={photo}
              alt={`${title} ${index + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
              }}
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-sm font-medium">Click to view</span>
            </div>
          </div>
        ))}
      </div>

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20"
            onClick={() => setSelectedPhoto(null)}
          >
            <XCircleIcon className="w-8 h-8 text-white" />
          </button>
          <img
            src={selectedPhoto}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

const LocationCard = ({ type, data, coordinates }) => {
  const isPickup = type === 'pickup';
  
  const openInMaps = () => {
    if (coordinates?.latitude && coordinates?.longitude) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`,
        '_blank'
      );
    } else if (data?.address) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}`,
        '_blank'
      );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${isPickup ? 'bg-green-100' : 'bg-red-100'} rounded-xl flex items-center justify-center`}>
            <MapPinIcon className={`w-5 h-5 ${isPickup ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <div>
            <p className={`text-sm font-medium ${isPickup ? 'text-green-700' : 'text-red-700'}`}>
              {isPickup ? 'PICKUP LOCATION' : 'DELIVERY LOCATION'}
            </p>
          </div>
        </div>
        <button
          onClick={openInMaps}
          className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
        >
          <MapIcon className="w-4 h-4" />
          Open Maps
        </button>
      </div>
      
      <div className="space-y-3">
        <p className="text-gray-900 font-medium">{data?.address || 'No address provided'}</p>
        
        {data?.landmark && (
          <p className="text-gray-600 text-sm flex items-center gap-2">
            <span className="text-lg">📍</span> {data.landmark}
          </p>
        )}
        
        {data?.contactName && (
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <UserIcon className="w-4 h-4" />
            <span>{data.contactName}</span>
          </div>
        )}
        
        {data?.contactPhone && (
          <a
            href={`tel:${data.contactPhone}`}
            className="flex items-center gap-2 text-green-700 text-sm hover:text-green-600"
          >
            <PhoneIcon className="w-4 h-4" />
            <span>{data.contactPhone}</span>
          </a>
        )}

        {coordinates?.latitude && coordinates?.longitude && (
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              GPS: {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
              {coordinates.accuracy && ` (±${Math.round(coordinates.accuracy)}m)`}
            </p>
            {coordinates.timestamp && (
              <p className="text-xs text-gray-500">
                Recorded: {new Date(coordinates.timestamp).toLocaleString('en-IN')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InvoiceSection = ({ task, invoice }) => {
  const data = invoice || task?.invoiceData;
  
  if (!data && !task?.invoiceGenerated) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <DocumentTextIcon className="w-5 h-5 text-gray-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Invoice</h3>
        </div>
        <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <p className="text-gray-500">Invoice not generated yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <DocumentTextIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Invoice</h3>
            <p className="text-gray-500 text-sm">{data?.invoiceNumber || task?.invoiceNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            <PrinterIcon className="w-5 h-5 text-gray-600" />
          </button>
          <button className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            <ArrowDownTrayIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {data?.items?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-gray-900 font-medium mb-3">Items</h4>
          <div className="bg-gray-50 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left text-gray-600 text-sm font-medium p-3">Item</th>
                  <th className="text-center text-gray-600 text-sm font-medium p-3">Qty</th>
                  <th className="text-right text-gray-600 text-sm font-medium p-3">Price</th>
                  <th className="text-right text-gray-600 text-sm font-medium p-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-200">
                    <td className="text-gray-900 p-3">{item.name}</td>
                    <td className="text-gray-600 text-center p-3">{item.quantity}</td>
                    <td className="text-gray-600 text-right p-3">₹{item.unitPrice?.toLocaleString('en-IN')}</td>
                    <td className="text-gray-900 text-right p-3 font-medium">₹{item.totalPrice?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data?.deselectedItems?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-red-700 font-medium mb-3 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5" />
            Missing/Deselected Items
          </h4>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            {data.deselectedItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <span className="text-red-700">{item.name}</span>
                <span className="text-red-600 text-sm px-2 py-1 bg-red-100 rounded">
                  {item.reason === 'not_available' ? 'Not Available' : 
                   item.reason === 'damaged' ? 'Damaged' : 'Wrong Item'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Original Amount</span>
          <span className="text-gray-900">₹{(data?.payment?.originalAmount || task?.payment?.amount)?.toLocaleString('en-IN')}</span>
        </div>
        {data?.payment?.discount > 0 && (
          <div className="flex justify-between">
            <span className="text-red-700">Deductions</span>
            <span className="text-red-700">-₹{data.payment.discount.toLocaleString('en-IN')}</span>
          </div>
        )}
        <div className="flex justify-between pt-3 border-t border-gray-300">
          <span className="text-gray-900 font-bold">Amount Collected</span>
          <span className="text-green-700 font-bold text-xl">
            ₹{(data?.payment?.adjustedAmount || task?.payment?.collectedAmount || task?.payment?.amount)?.toLocaleString('en-IN')}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Payment Mode</span>
          <span className="text-gray-900">{(data?.payment?.mode || task?.payment?.mode)?.toUpperCase()}</span>
        </div>
        {data?.payment?.collectedAt && (
          <div className="flex justify-between">
            <span className="text-gray-600">Collected At</span>
            <span className="text-gray-700 text-sm">
              {new Date(data.payment.collectedAt).toLocaleString('en-IN')}
            </span>
          </div>
        )}
      </div>

      {data?.completedInGracePeriod && (
        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl">
          <p className="text-orange-700 text-sm flex items-center gap-2">
            <ClockIcon className="w-4 h-4" />
            Completed during grace period
          </p>
        </div>
      )}
    </div>
  );
};

const Timeline = ({ events }) => {
  if (!events || events.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Timeline</h3>
        <p className="text-gray-500 text-center py-8">No timeline events</p>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'created': return '📝';
      case 'assigned': return '👤';
      case 'accepted': return '✅';
      case 'picked_up': return '📦';
      case 'in_transit': return '🚚';
      case 'delivered': return '🎉';
      case 'failed': return '❌';
      default: return '⏱️';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <h3 className="text-lg font-bold text-gray-900 mb-6">Timeline</h3>
      <div className="space-y-4">
        {events.map((event, idx) => (
          <div key={idx} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-lg">
                {getStatusIcon(event.status)}
              </div>
              {idx < events.length - 1 && (
                <div className="w-0.5 h-full bg-gray-300 mt-2"></div>
              )}
            </div>
            <div className="flex-1 pb-4">
              <p className="text-gray-900 font-medium">
                {event.status?.replace('_', ' ').toUpperCase()}
              </p>
              <p className="text-gray-500 text-sm">
                {event.timestamp ? new Date(event.timestamp).toLocaleString('en-IN') : '-'}
              </p>
              {event.note && (
                <p className="text-gray-600 text-sm mt-1">{event.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId) {
      navigate('/tasks');
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'tasks', taskId),
      (docSnap) => {
        if (docSnap.exists()) {
          setTask({ id: docSnap.id, ...docSnap.data() });
        } else {
          navigate('/tasks');
        }
        setLoading(false);
      },
      (error) => {
        console.error('Task subscription error:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [taskId, navigate]);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (task?.invoiceId) {
        try {
          const invoiceQuery = query(collection(db, 'invoices'), where('taskId', '==', taskId));
          const invoiceDocs = await getDocs(invoiceQuery);
          if (!invoiceDocs.empty) {
            setInvoice({ id: invoiceDocs.docs[0].id, ...invoiceDocs.docs[0].data() });
          }
        } catch (error) {
          console.error('Invoice fetch error:', error);
        }
      }
    };

    if (task) fetchInvoice();
  }, [task, taskId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <TruckIcon className="w-24 h-24 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Task Not Found</h2>
        <Link to="/tasks" className="text-red-600 hover:text-red-700">
          Go back to Tasks
        </Link>
      </div>
    );
  }

  const pickupPhotos = [
    ...(task.pickupProofUrls || []),
    ...(task.pickupProofUrl ? [task.pickupProofUrl] : []),
    ...(task.invoiceData?.delivery?.pickupPhotos || []),
  ].filter((url, index, self) => url && self.indexOf(url) === index); // Remove duplicates and empty

  const deliveryPhotos = [
    ...(task.deliveryProofUrls || []),
    ...(task.deliveryProofUrl ? [task.deliveryProofUrl] : []),
    ...(task.invoiceData?.delivery?.deliveryPhotos || []),
  ].filter((url, index, self) => url && self.indexOf(url) === index); // Remove duplicates and empty

  const failurePhotos = [
    ...(task.failureProofUrls || []),
  ].filter(url => url);

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{task.invoiceNumber}</h1>
                <p className="text-gray-500 text-sm">
                  Created: {task.createdAt ? new Date(task.createdAt).toLocaleString('en-IN') : '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <StatusBadge status={task.status} />
              <PaymentBadge mode={task.payment?.mode} collected={task.payment?.collected} />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center">
                  <UserIcon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">{task.customer?.name}</h2>
                  <p className="text-gray-600">{task.customer?.phone}</p>
                </div>
                <a 
                  href={`tel:${task.customer?.phone}`}
                  className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-xl flex items-center gap-2 transition-colors"
                >
                  <PhoneIcon className="w-5 h-5" />
                  Call
                </a>
              </div>
              {task.customer?.email && (
                <p className="text-gray-600 text-sm">📧 {task.customer.email}</p>
              )}
            </div>

            {/* Porter Info */}
            {task.porterId && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Assigned Porter</h3>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <TruckIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">{task.porterName || 'Unknown'}</p>
                    <p className="text-gray-500 text-sm">Porter ID: {task.porterId}</p>
                  </div>
                  {task.acceptedAt && (
                    <div className="text-right">
                      <p className="text-gray-500 text-sm">Accepted</p>
                      <p className="text-gray-900 text-sm">
                        {new Date(task.acceptedAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Locations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <LocationCard type="pickup" data={task.pickup} coordinates={task.pickupLocation} />
              <LocationCard type="delivery" data={task.delivery} coordinates={task.deliveryLocation} />
            </div>

            {/* Package Details */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Package Details</h3>
              <div className="space-y-4">
                {task.package?.description && (
                  <div>
                    <p className="text-gray-500 text-sm">Description</p>
                    <p className="text-gray-900">{task.package.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {task.distance && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-500 text-xs">Distance</p>
                      <p className="text-gray-900 font-bold">{task.distance}</p>
                    </div>
                  )}
                  {task.estimatedTime && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-500 text-xs">Est. Time</p>
                      <p className="text-gray-900 font-bold">{task.estimatedTime}</p>
                    </div>
                  )}
                  {task.package?.weight && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-500 text-xs">Weight</p>
                      <p className="text-gray-900 font-bold">{task.package.weight}</p>
                    </div>
                  )}
                  {task.deadlineMinutes && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-500 text-xs">Time Limit</p>
                      <p className="text-gray-900 font-bold">{task.deadlineMinutes} mins</p>
                    </div>
                  )}
                </div>

                {task.package?.isFragile && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <p className="text-yellow-700 text-sm font-medium">⚠️ FRAGILE - Handle with care</p>
                  </div>
                )}

                {task.package?.specialInstructions && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-blue-700 text-sm">📝 {task.package.specialInstructions}</p>
                  </div>
                )}

                {task.package?.products?.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2">Products ({task.package.products.length})</p>
                    <div className="bg-gray-50 rounded-xl overflow-hidden">
                      {task.package.products.map((product, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 border-b border-gray-200 last:border-0"
                        >
                          <div>
                            <p className="text-gray-900">{product.name}</p>
                            <p className="text-gray-500 text-sm">Qty: {product.quantity}</p>
                          </div>
                          <p className="text-gray-900 font-medium">
                            ₹{(product.price * product.quantity).toLocaleString('en-IN')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <PhotoGallery title="Pickup Photos" photos={pickupPhotos} icon={CameraIcon} />
            <PhotoGallery title="Delivery Photos" photos={deliveryPhotos} icon={CheckCircleIcon} />
            {failurePhotos.length > 0 && (
              <PhotoGallery title="Failure/Issue Photos" photos={failurePhotos} icon={ExclamationTriangleIcon} />
            )}

            {task.status === 'failed' && task.failureReason && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
                  <XCircleIcon className="w-6 h-6" />
                  Failure Reason
                </h3>
                <p className="text-gray-900">{task.failureReason}</p>
                {task.failedAt && (
                  <p className="text-gray-500 text-sm mt-2">
                    Failed at: {new Date(task.failedAt).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Summary</h3>
              <div className="text-center">
                <p className="text-gray-500 text-sm">Amount</p>
                <p className="text-4xl font-bold text-gray-900 mt-1">
                  ₹{task.payment?.amount?.toLocaleString('en-IN')}
                </p>
                <PaymentBadge mode={task.payment?.mode} collected={task.payment?.collected} />
              </div>
              
              {task.payment?.collected && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Collected Amount</span>
                    <span className="text-green-700 font-bold">
                      ₹{task.payment.collectedAmount?.toLocaleString('en-IN')}
                    </span>
                  </div>
                  {task.payment.collectedAt && (
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(task.payment.collectedAt).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
              )}
            </div>

            <InvoiceSection task={task} invoice={invoice} />
            <Timeline events={task.timeline} />

            {task.deselectedItems?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-red-700 mb-4">Missing Items Reported</h3>
                {task.deselectedItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-red-200 last:border-0">
                    <span className="text-gray-900">{item.name}</span>
                    <span className="text-red-600 text-sm">{item.reason}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Timestamps</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span className="text-gray-900">{task.createdAt ? new Date(task.createdAt).toLocaleString('en-IN') : '-'}</span>
                </div>
                {task.acceptedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Accepted</span>
                    <span className="text-gray-900">{new Date(task.acceptedAt).toLocaleString('en-IN')}</span>
                  </div>
                )}
                {task.pickedUpAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Picked Up</span>
                    <span className="text-gray-900">{new Date(task.pickedUpAt).toLocaleString('en-IN')}</span>
                  </div>
                )}
                {task.deliveredAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Delivered</span>
                    <span className="text-green-700">{new Date(task.deliveredAt).toLocaleString('en-IN')}</span>
                  </div>
                )}
                {task.failedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Failed</span>
                    <span className="text-red-700">{new Date(task.failedAt).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskDetails;